import { useState } from "react"
import { addDays, addMonths, differenceInCalendarDays, eachDayOfInterval, endOfMonth, endOfWeek, format, isBefore, isSameMonth, isToday, parseISO, startOfDay, startOfMonth, startOfWeek } from "date-fns"
import { ChevronLeft, ChevronRight, StickyNote } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { CLEAN_KIND_COLOURS, CLEAN_KIND_LABELS } from "~/components/CleanKindFields"

const WEEK_OPTS = { weekStartsOn: 1 }
const WEEKDAYS = [ "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun" ]

// One strip per clean per week, spanning every day of its window (two for a
// changeover, the whole window for "cleaner to confirm"), coloured by kind.
function stripsFor(cleans, weekStart) {
  const weekEnd = addDays(weekStart, 6)
  const rows = []
  return cleans
    .map((c) => {
      const start = parseISO(c.window_starts_on)
      const end = parseISO(c.window_ends_on || c.window_starts_on)
      if (end < weekStart || start > weekEnd) return null
      const first = start < weekStart ? weekStart : start
      const last = end > weekEnd ? weekEnd : end
      return { clean: c, col: differenceInCalendarDays(first, weekStart), span: differenceInCalendarDays(last, first) + 1, startsHere: start >= weekStart, endsHere: end <= weekEnd }
    })
    .filter(Boolean)
    .sort((a, b) => a.col - b.col || b.span - a.span)
    .map((seg) => {
      let row = rows.findIndex((free) => free <= seg.col)
      if (row === -1) row = rows.push(0) - 1
      rows[row] = seg.col + seg.span
      return { ...seg, row }
    })
}

// Each cleaner has one property, so the strip only needs the timing.
function stripLabel(clean) {
  if (clean.kind === "back_to_back") return "11am–3pm"
  if (clean.kind === "changeover") return "Either day"
  if (clean.kind === "cleaner_chooses") return "Pick a day"
  return clean.scheduled_time || "Clean"
}

// The cleaner's month at a glance: strips for their cleans (colour = kind of
// clean), Jeremy's day notes, and a legend. Tapping a strip opens the clean.
export default function PortalCalendar({ cleans, notes = [], onPick }) {
  const [ month, setMonth ] = useState(() => startOfMonth(new Date()))
  const today = startOfDay(new Date())
  const gridStart = startOfWeek(startOfMonth(month), WEEK_OPTS)
  const gridEnd = endOfWeek(endOfMonth(month), WEEK_OPTS)
  const weeks = []
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 7)) weeks.push(d)
  const noteRows = weeks.map((weekStart) => notes.filter((n) => { const d = parseISO(n.on_date); return d >= weekStart && d <= addDays(weekStart, 6) }))
  const stripRows = weeks.map((weekStart) => stripsFor(cleans, weekStart))
  const stripHeight = Math.max(1, ...stripRows.map((r) => r.reduce((m, s) => Math.max(m, s.row + 1), 0)))

  return (
    <div className="rounded-xl border border-border bg-card p-3 shadow-sm">
      <div className="mb-2 flex items-center justify-between">
        <Button variant="ghost" size="icon-sm" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month"><ChevronLeft /></Button>
        <p className="text-sm font-semibold text-foreground">{format(month, "MMMM yyyy")}</p>
        <Button variant="ghost" size="icon-sm" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month"><ChevronRight /></Button>
      </div>

      <div className="grid grid-cols-7 text-center text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {WEEKDAYS.map((d) => <div key={d} className="py-1">{d}</div>)}
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        {weeks.map((weekStart, w) => {
          const days = eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) })
          return (
            <div key={weekStart.toISOString()} className="relative border-b border-border last:border-b-0">
              <div aria-hidden className="pointer-events-none absolute inset-0 grid grid-cols-7">
                {days.map((day) => (
                  <div key={day.toISOString()} className={cn("border-r border-border/50 last:border-r-0", isBefore(day, today) && "bg-muted/50", isToday(day) && "bg-brand/10")} />
                ))}
              </div>
              <div className="relative grid grid-cols-7">
                {days.map((day) => (
                  <div key={day.toISOString()} className={cn("px-1 pt-1 text-right text-[11px] font-medium", !isSameMonth(day, month) && "opacity-40", isToday(day) ? "font-bold text-brand" : "text-muted-foreground")}>
                    {format(day, "d")}
                  </div>
                ))}
              </div>
              <div className="relative mt-0.5 grid grid-cols-7 gap-y-0.5 px-0.5 pb-1" style={{ gridTemplateRows: `repeat(${stripHeight}, 1.25rem)` }}>
                {stripRows[w].map((seg) => (
                  <button
                    key={seg.clean.id}
                    type="button"
                    onClick={() => onPick?.(seg.clean)}
                    title={`${seg.clean.property_short_name} — ${seg.clean.when_label}`}
                    style={{ gridColumn: `${seg.col + 1} / span ${seg.span}`, gridRow: seg.row + 1 }}
                    className={cn(
                      "flex h-5 items-center truncate border px-1.5 text-left text-[10px] font-semibold leading-none",
                      CLEAN_KIND_COLOURS[seg.clean.kind] || CLEAN_KIND_COLOURS.scheduled,
                      seg.clean.status === "done" && "opacity-50",
                      seg.startsHere && "ml-0.5 rounded-l",
                      seg.endsHere && "mr-0.5 rounded-r",
                    )}
                  >
                    {seg.startsHere && <span className="truncate">{stripLabel(seg.clean)}</span>}
                  </button>
                ))}
              </div>
              {noteRows[w].length > 0 && (
                <div className="relative grid grid-cols-7 gap-0.5 px-0.5 pb-1">
                  {noteRows[w].map((note) => (
                    <div
                      key={note.id}
                      style={{ gridColumn: `${differenceInCalendarDays(parseISO(note.on_date), weekStart) + 1} / span 1` }}
                      title={note.body}
                      className="flex items-center gap-0.5 truncate rounded border border-amber-300 bg-amber-100 px-1 py-0.5 text-[10px] font-medium leading-none text-amber-900 dark:border-amber-700 dark:bg-amber-900/40 dark:text-amber-100"
                    >
                      <StickyNote size={9} className="shrink-0" />
                      <span className="truncate">{note.body}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        {Object.entries(CLEAN_KIND_LABELS).map(([ kind, label ]) => (
          <span key={kind}><span className={cn("mr-1 inline-block size-2.5 rounded-sm border align-middle", CLEAN_KIND_COLOURS[kind])} />{label}</span>
        ))}
        <span><span className="mr-1 inline-block size-2.5 rounded-sm border border-amber-300 bg-amber-100 align-middle" />note from Jeremy</span>
      </p>
    </div>
  )
}
